import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import { api, getUser } from "../lib/api";

/**
 * Very small safe markdown renderer:
 * - **bold** -> <strong>
 * - *italic* -> <em>
 * - `code` -> <code>
 * - line breaks preserved
 * - links [text](url) -> <a>
 */
function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function renderMarkdown(text) {
  if (!text) return null;
  let html = escapeHtml(text);
  html = html.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+|\/[^)]*)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  html = html.replace(/\n/g, "<br/>");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * Floating AI Chat widget. Fixed bottom-right.
 * When `lessonId` is passed via prop, acts as Study Buddy for that lesson.
 * Otherwise acts as Ads Consultant.
 */
export default function AiChatWidget({ lessonId = "", buttonLabel }) {
  const user = getUser();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const sessionRef = useRef(
    lessonId
      ? `lesson-${lessonId}`
      : `ads-${new Date().toISOString().slice(0, 10)}`
  );

  useEffect(() => {
    if (!open || !user?.id) return;
    api(`/ai/history/${sessionRef.current}`)
      .then((h) => setMsgs(h))
      .catch(() => setMsgs([]));
  }, [open, user?.id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (!user?.id) {
      window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", streaming: true }]);

    const token = localStorage.getItem("mastofa_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/ai/chat/stream`;
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: sessionRef.current,
          message: text,
          context: lessonId ? "study-buddy" : "ads-consultant",
          lesson_id: lessonId,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          const line = frame.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const chunk = line.slice(6);
          if (chunk === "[DONE]") continue;
          const restored = chunk.replace(/\\n/g, "\n");
          setMsgs((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") {
              copy[copy.length - 1] = { ...last, content: last.content + restored, streaming: true };
            }
            return copy;
          });
        }
      }
      setMsgs((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") {
          copy[copy.length - 1] = { ...last, streaming: false };
        }
        return copy;
      });
    } catch (e) {
      setMsgs((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            role: "assistant",
            content: "Maaf, AI sedang tidak tersedia. Coba lagi.",
            streaming: false,
          };
        }
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const suggestions = lessonId
    ? [
        { label: "🤔 Saya bingung", text: "Saya bingung dengan materi ini. Tolong jelaskan dengan bahasa yang lebih sederhana + kasih 1 contoh analogi sehari-hari yang mudah dipahami." },
        { label: "🧪 Tes saya", text: "Buatkan saya 3 pertanyaan singkat untuk menguji pemahaman saya tentang materi ini. Jangan kasih jawaban langsung — saya akan jawab dulu, baru koreksi." },
        { label: "📌 Beri contoh", text: "Beri 2 contoh penerapan nyata materi ini di bisnis kecil (misal jualan online marketplace)." },
        { label: "📝 Ringkas 3 poin", text: "Ringkas materi lesson ini dalam 3 poin utama." },
        { label: "📚 Prasyarat", text: "Apa istilah/konsep yang harus saya pahami dulu sebelum materi ini?" },
      ]
    : [
        { label: "ROAS rendah", text: "ROAS TikTok saya 0.8, apa yang harus dicek?" },
        { label: "Budget Meta", text: "Budget iklan Meta pemula sebaiknya berapa?" },
        { label: "CTWA vs LP", text: "Perbedaan CTWA vs Landing Page?" },
      ];

  return (
    <>
      {!open && (
        <button
          className="ai-fab"
          onClick={() => setOpen(true)}
          data-testid="ai-chat-fab"
          aria-label="Buka Mas Tofa AI"
        >
          <Sparkles size={18} />
          <span>{buttonLabel || (lessonId ? "Tanya AI tentang lesson" : "Mas Tofa AI")}</span>
        </button>
      )}
      {open && (
        <div className="ai-panel" data-testid="ai-chat-panel">
          <header className="ai-panel-head">
            <div>
              <b>{lessonId ? "Study Buddy" : "Mas Tofa AI"}</b>
              <small>{lessonId ? "Bantuan materi lesson" : "Konsultan iklan digital 24/7"}</small>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-chat-close" aria-label="Tutup">
              <X size={18} />
            </button>
          </header>
          <div className="ai-msgs" ref={listRef} data-testid="ai-msgs">
            {msgs.length === 0 && (
              <div className="ai-empty">
                <Sparkles size={22} />
                <p>
                  Halo{user?.name ? ` ${user.name.split(" ")[0]}` : ""}! Tanya apa saja tentang
                  {lessonId ? " materi ini" : " Meta Ads, TikTok Ads, atau Shopee Ads"}.
                </p>
                <div className="ai-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => {
                        setInput(s.text);
                        // Auto-send for quick actions
                        setTimeout(() => {
                          const form = document.querySelector('.ai-input');
                          form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                        }, 100);
                      }}
                      data-testid={`ai-sug-${s.label.replace(/\W+/g, '-').toLowerCase()}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`ai-msg ai-msg-${m.role}`}>
                {m.role === "assistant"
                  ? renderMarkdown(m.content)
                  : m.content}
                {m.streaming && <span className="ai-cursor" />}
              </div>
            ))}
            {busy && msgs[msgs.length - 1]?.role !== "assistant" && (
              <div className="ai-msg ai-msg-assistant ai-typing">AI sedang mengetik…</div>
            )}
          </div>
          <form
            className="ai-input"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={user ? "Ketik pertanyaan…" : "Login untuk chat AI"}
              disabled={busy || !user}
              data-testid="ai-chat-input"
            />
            <button type="submit" disabled={busy || !input.trim() || !user} data-testid="ai-chat-send">
              <Send size={16} />
            </button>
          </form>
          <small className="ai-disclaimer">
            AI dapat membuat kesalahan. Untuk bedah akun mendalam, ambil{" "}
            <a href="/privat" target="_blank" rel="noreferrer">Privat 1-on-1</a>.
          </small>
        </div>
      )}
    </>
  );
}
